import React, { useCallback, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import { MemoryRouter, Route } from "react-router-dom";
import ViewInvoice from "../billing-payment/ViewInvoice";

const iconBtnSx = {
  bgcolor: "#21cdc0",
  color: "#fff",
  borderRadius: "10px",
  width: 44,
  height: 44,
  "&:hover": { bgcolor: "#1bb5aa" },
  "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#e2e8f0" },
};

/**
 * Invoice preview dialog (~80vw × 80vh) embedding `ViewInvoice` via in-memory route state.
 * Compact PDF / print / paper-size controls live in the header only for this dialog.
 */
export default function AppointmentInvoicePreviewDialog({
  open,
  onClose,
  invoiceNo,
  receiptNo,
  locationState,
}) {
  const pathSlug = String(invoiceNo || "preview").replace(
    /[^a-zA-Z0-9-_.]/g,
    "_"
  );
  const path = `/viewInvoice/${pathSlug}`;

  const [pdfToolbarApi, setPdfToolbarApi] = useState(null);
  const downloadBtnRef = useRef(null);
  const printBtnRef = useRef(null);

  const onPdfToolbarApiReady = useCallback((api) => {
    setPdfToolbarApi(api);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          zIndex: 14000,
          width: "80vw",
          maxWidth: "80vw",
          height: "80vh",
          maxHeight: "80vh",
          m: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f5f5f5",
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          backgroundColor: "#213360",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          py: 1.25,
          px: 2,
        }}
      >
        <Box sx={{ color: "#fff", flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={{ fontWeight: 700, fontSize: "1rem" }}>
            Invoice preview
          </Typography>
          <Typography component="div" variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
            Invoice: {invoiceNo || "—"}
            {receiptNo && receiptNo !== "N/A" ? ` · Receipt: ${receiptNo}` : ""}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <IconButton
            ref={downloadBtnRef}
            size="small"
            aria-label="Download PDF"
            sx={iconBtnSx}
            disabled={!pdfToolbarApi?.canDownloadPDF}
            onClick={() =>
              pdfToolbarApi?.openDownloadMenu?.(downloadBtnRef.current)
            }
          >
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
          <IconButton
            ref={printBtnRef}
            size="small"
            aria-label="Print"
            sx={iconBtnSx}
            disabled={!pdfToolbarApi?.canDownloadPDF}
            onClick={() => pdfToolbarApi?.openPrintMenu?.(printBtnRef.current)}
          >
            <PrintIcon fontSize="small" />
          </IconButton>
          <FormControl
            size="small"
            variant="outlined"
            sx={{
              minWidth: { xs: 160, sm: 200 },
              bgcolor: "#fff",
              borderRadius: "10px",
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            disabled={!pdfToolbarApi}
          >
            <InputLabel id="preview-paper-size-label">Paper size</InputLabel>
            <Select
              labelId="preview-paper-size-label"
              id="preview-paper-size"
              label="Paper size"
              value={pdfToolbarApi?.invoicePaperSize ?? "a5"}
              onChange={(e) =>
                pdfToolbarApi?.setInvoicePaperSize?.(e.target.value)
              }
            >
              <MenuItem value="a4">A4 (210 × 297 mm)</MenuItem>
              <MenuItem value="a5">A5 (148 × 210 mm)</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={onClose} aria-label="Close preview" sx={{ color: "#fff" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          {locationState?.data ? (
            <MemoryRouter
              initialEntries={[
                {
                  pathname: path,
                  state: locationState,
                },
              ]}
            >
              <Route
                path="/viewInvoice/:id"
                render={() => (
                  <ViewInvoice
                    mode="invoice"
                    externalPdfToolbar
                    onPdfToolbarApiReady={onPdfToolbarApiReady}
                  />
                )}
              />
            </MemoryRouter>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography>Loading preview…</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
